import React, { useState, useEffect } from 'react';
import styles from './Modal.module.css';
import { PlanningItem, BACKWARD_PLANNING_TEMPLATES } from './types';

interface BackwardPlanningModalProps {
  onClose: () => void;
  onApply: (items: Omit<PlanningItem, 'id'>[]) => void;
}

export const BackwardPlanningModal: React.FC<BackwardPlanningModalProps> = ({ onClose, onApply }) => {
  const [selectedType, setSelectedType] = useState<'nieuwsbrief' | 'bijeenkomst' | 'social'>('nieuwsbrief');
  const [deadline, setDeadline] = useState('');
  const [calculatedSteps, setCalculatedSteps] = useState<{ date: string; name: string; days: number }[]>([]);

  useEffect(() => {
    if (deadline) {
      calculateDates();
    }
  }, [deadline, selectedType]);

  const calculateDates = () => {
    const template = BACKWARD_PLANNING_TEMPLATES.find(t => t.type === selectedType);
    if (!template || !deadline) return;

    const deadlineDate = new Date(deadline);
    const steps = template.steps.map(step => {
      const stepDate = new Date(deadlineDate);
      stepDate.setDate(stepDate.getDate() + step.days);
      
      return {
        date: stepDate.toISOString().split('T')[0],
        name: step.name,
        days: step.days
      };
    });

    setCalculatedSteps(steps);
  };

  const handleApply = () => {
    if (!deadline || calculatedSteps.length === 0) return;

    const items: Omit<PlanningItem, 'id'>[] = calculatedSteps.map(step => ({
      type: step.days === 0 ? 'deadline' : 'communicatie',
      title: step.name,
      date: step.date,
      time: '09:00',
      targetAudience: 'Stakeholders',
      channel: selectedType === 'nieuwsbrief' ? 'Email' : 
               selectedType === 'bijeenkomst' ? 'Fysieke bijeenkomst' : 
               'Social Media',
      responsible: 'Communicatieteam',
      status: 'Gepland',
      dependencies: []
    }));

    onApply(items);
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'nieuwsbrief':
        return 'Nieuwsbrief';
      case 'bijeenkomst':
        return 'Bewonersbijeenkomst';
      case 'social':
        return 'Social media campagne';
      default:
        return type;
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Terugrekenen vanaf deadline</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className={styles.modalContent}>
          <div className={styles.formGroup}>
            <label>Type middel</label>
            <div className={styles.typeSelector}>
              {BACKWARD_PLANNING_TEMPLATES.map(template => (
                <button
                  key={template.type}
                  className={`${styles.typeButton} ${selectedType === template.type ? styles.active : ''}`}
                  onClick={() => setSelectedType(template.type)}
                >
                  {getTypeLabel(template.type)}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="deadline">Deadline</label>
            <input
              id="deadline"
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              required
            />
          </div>

          {calculatedSteps.length > 0 && (
            <div className={styles.stepsPreview}>
              <h3>Voorgestelde planning</h3>
              <div className={styles.stepsList}>
                {calculatedSteps.map((step, index) => {
                  const date = new Date(step.date);
                  const isDeadline = step.days === 0;
                  
                  return (
                    <div 
                      key={index} 
                      className={`${styles.stepItem} ${isDeadline ? styles.deadline : ''}`}
                    >
                      <div className={styles.stepDate}>
                        <div className={styles.dayNumber}>{date.getDate()}</div>
                        <div className={styles.monthName}>
                          {date.toLocaleDateString('nl-NL', { month: 'short' })}
                        </div>
                      </div>
                      <div className={styles.stepContent}>
                        <div className={styles.stepName}>{step.name}</div>
                        <div className={styles.stepMeta}>
                          {step.days === 0 ? 'Deadline' : `${Math.abs(step.days)} dagen voor deadline`}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className={styles.infoBox}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            <p>
              Op basis van de gekozen deadline worden automatisch alle voorbereidende stappen 
              toegevoegd aan de planning. U kunt deze achteraf nog aanpassen.
            </p>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose}>
            Annuleren
          </button>
          <button 
            className={styles.btnPrimary} 
            onClick={handleApply}
            disabled={!deadline || calculatedSteps.length === 0}
          >
            Toepassen ({calculatedSteps.length} items)
          </button>
        </div>
      </div>
    </div>
  );
};